import React from 'react';
import {
    Alert,
    Box,
    Container,
    Button,
    Link,
    Typography
} from '@mui/material';
import {
    ArrowBack,
    SupportAgent
} from '@mui/icons-material';
import dayjs from "dayjs";
import {IraSearchHistoryTypes, NotificationTypes, ReportTypes, RiskReportTypes, UserTypes} from "../../../../../types";
import {store} from "../../../../../store";
import {useEffectiveSettings, SETTING} from "../../../../../hooks/use-effective-settings";
import {FlagResultDialog} from "../flag-dialog";
import {canUseIraSearchHistory} from "../../../../../helpers/permissionCheck";
import {toast} from "react-toastify";
import {api} from "../../../../../http";
import {useGenericQuery} from "../../../../../services/react-query/generic.query";
import {NotifyTeamDialog} from "../notify-team-dialog";
import {RiskPatientInfoPanel} from "../../../../../components/risk-assessments/risk-patient-info-panel";
import {LocationAndArea} from "../../../../../components/risk-assessments/location-and-area";
import {PublicRecords} from "../../../../../components/risk-assessments/public-records";
import {SubjectOffenderMatch} from "../../../../../components/risk-assessments/subject-offender-match";
import {OffendersAtLocation} from "../../../../../components/risk-assessments/offenders-at-location";
import {NewsArticles} from "../../../../../components/risk-assessments/news-articles";
import {getPatientName} from "../../../../../helpers/crimeScore";
import SaveReportConfirmDialog from "../../../../../components/save-report-confirm-dialog";
import {RiskFactorsAccordionSection} from "../../../../../components/risk-assessments/risk-factors";


interface Props {
    setCurrentView?: (view: 'search' | 'details') => void;
    assessmentData: RiskReportTypes.RiskResponse;
    address : RiskReportTypes.Address
    handleDownload?: (data: RiskReportTypes.RiskResponse) => void;
    isView : boolean;
    report?: ReportTypes.Model | null;
    // Reopened from All Searches (868mkwmk5), with any flags already sent on it.
    isPastSearch?: boolean;
    flags?: IraSearchHistoryTypes.FlagRow[];
}

export const RiskAssessmentResultPage: React.FC<Props> = ({ setCurrentView, assessmentData, address, handleDownload, isView, report, isPastSearch, flags }) => {

    // Read from the store, not useSelector: the PDF export renders this page in
    // a separate root with no redux Provider (helpers/downloadPdf).
    const isMasquerading = !!store.getState().auth?.authUser?.masquarade;
    // POM staff: POM master, or support masquerading into the org.
    const isPomStaff = isMasquerading || !!store.getState().auth?.authUser?.pomMaster;
    const { getSetting, isLoading: settingsLoading } = useEffectiveSettings();
    const [openFlagDialog, setOpenFlagDialog] = React.useState<boolean>(false);
    // References of reports sent to POM Support from this page view.
    const [sessionReferences, setSessionReferences] = React.useState<string[]>([]);
    // A saved report is flagged as the report; anything else as the stored
    // search node-api returned with it. No target means nothing to point at.
    const flagTarget: IraSearchHistoryTypes.FlagTarget | null = report?.id
        ? { reportId: report.id }
        : assessmentData?.searchId ? { searchId: assessmentData.searchId } : null;
    // "N charges filtered out · View" under Criminal Records. Recorded on every
    // search: POM staff see it on any result, the org's own users only when POM
    // has turned it on for the org. pom-2-api already leaves it out of the
    // payload for everyone else; this keeps the page consistent with that.
    const showFilteredCharges = isPomStaff || (!settingsLoading && getSetting<boolean>(SETTING.iraFilteredCharges, false));
    // Hidden while masquerading: the flag would be sent as the customer's user.
    const canFlag = !settingsLoading
        && getSetting<boolean>(SETTING.iraSearchHistory, false)
        && !!flagTarget
        && !isMasquerading
        && canUseIraSearchHistory();

    const [isSaveReportLoading, setIsSaveReportLoading] = React.useState<boolean>(false);
    const [openNotifyDialog, setOpenNotifyDialog] = React.useState<boolean>(false);
    const [openSaveConfirmationDialog, setOpenSaveConfirmationDialog] = React.useState<boolean>(false);
    // Users notified during this session, merged below with those already on
    // the saved report so the panel shows the full audit trail immediately.
    const [sessionNotified, setSessionNotified] = React.useState<NotificationTypes.NotifiedUser[]>([]);

    const { AreaCrimeScore, sexOffenderNearby, sexOffenderAtLocation } = assessmentData;

    // Saved reports carry notifiedUsersId (ObjectId strings, cumulative across
    // saves); resolve names from the subscription user list the notify dialog
    // uses anyway. Unresolvable ids belong to since-removed users.
    const { data: subscriptionUsers } = useGenericQuery<UserTypes.Model[]>('fetchUsersToNotify', 'users/getSubscriptionUsers');

    // First-notified timestamps come from ViewedReports via the notifications
    // endpoint; ids with no row predate the endpoint's data or were dropped by
    // the old first-save bug, so their chips just omit the date.
    const [notificationTimes, setNotificationTimes] = React.useState<Record<string, string>>({});
    const fetchNotificationTimes = React.useCallback(() => {
        if (!report?.id) return;
        api.get<{ result: { userId: string; notifiedAt: string | null }[] }>(`reports/notifications/${report.id}`)
            .then(({ data }) => {
                const times: Record<string, string> = {};
                (data?.result ?? []).forEach((row) => {
                    if (row.userId && row.notifiedAt) times[row.userId] = row.notifiedAt;
                });
                setNotificationTimes(times);
            })
            .catch((e) => {
                // Timestamps are decoration; the strip still renders without
                // them. Logged so an endpoint-wide failure is diagnosable.
                console.error('[RiskAssessmentResult] notifications fetch failed:', e);
            });
    }, [report?.id]);
    React.useEffect(() => {
        fetchNotificationTimes();
    }, [fetchNotificationTimes]);

    const reportNotified: NotificationTypes.NotifiedUser[] = subscriptionUsers
        ? (report?.notifiedUsersId ?? []).map((id) => {
            const user = subscriptionUsers.find((candidate) => candidate.id === id);
            return {
                id,
                name: user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : 'Removed user',
                notifiedAt: notificationTimes[id] ?? null,
            };
        })
        : [];
    const notifiedUsers: NotificationTypes.NotifiedUser[] = [
        ...reportNotified,
        ...sessionNotified
            .filter((session) => !reportNotified.some((existing) => existing.id === session.id))
            // Prefer the server-recorded time once the refetch lands; the
            // client-clock stamp is only the optimistic placeholder.
            .map((session) => ({ ...session, notifiedAt: notificationTimes[session.id] ?? session.notifiedAt })),
    ];

    const saveReports = async (selectedUsers : UserTypes.Model[], isNotify : boolean, goBack : boolean = false) => {
        setIsSaveReportLoading(true);
        let offenderAtLocation = sexOffenderAtLocation[0] || {};
        let requestObject: ReportTypes.ReportData = {
            place : {
                ...address
            },
            reportType : 'Intake Risk Assessment',
            reportComment : assessmentData?.riskAssessmentInfo?.summary ?? 'N/A',
            address : address.address,
            offenderName : getPatientName(assessmentData),
            crimeScore : AreaCrimeScore
        }
        try{
            let notifiedUsersId : string[] = [];

            if(offenderAtLocation && Object.keys(offenderAtLocation).length){
                requestObject.place.location = {
                    latitude : offenderAtLocation.sex_offender_lat,
                    longitude : offenderAtLocation.sex_offender_lon
                }
                requestObject.sexOffender = {
                    sexOffenderNearByCount : sexOffenderNearby,
                    sexOffendersAtLocation : sexOffenderAtLocation
                }
                requestObject.latitude = offenderAtLocation.sex_offender_lat
                requestObject.longitude = offenderAtLocation.sex_offender_lon
            }
            requestObject.riskAssessmentResult = assessmentData;
            if(isNotify && selectedUsers.length){
                selectedUsers.map((user)=>{
                    notifiedUsersId.push(user.id);
                });
                requestObject.notifiedUsersId = notifiedUsersId;
            }
        }catch(e){
            console.log("Error preparing request object to save report",e)
            toast("Error saving report", {
                autoClose: 1500,
                type: 'error',
            });
            setIsSaveReportLoading(false);
            return
        }

        let toastMessage = "Report saved";
        await api.post<
            {reportData:ReportTypes.ReportData},
            ReportTypes.ReportData>
        ('reports/saveReport',{reportData : requestObject})
            .then((data)=>{
                if(isNotify){
                    toastMessage = "Users are notified about the report";
                    setSessionNotified((prev) => [
                        ...prev,
                        ...selectedUsers
                            .filter((user) => !prev.some((existing) => existing.id === user.id))
                            .map((user) => ({
                                id: user.id,
                                name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
                                notifiedAt: new Date().toISOString(),
                            })),
                    ]);
                    setOpenNotifyDialog(false);
                    // The server writes ViewedReports on a 3s delay after the
                    // save response; refetch shortly after so the optimistic
                    // client timestamp converges to the server-recorded one.
                    setTimeout(fetchNotificationTimes, 4000);
                }
                // navigate("/reports/past-reports");
                toast(toastMessage, {
                    autoClose: 1500,
                    type: 'success',
                });
            })
            .catch((error)=>{
                console.log("Error",error)
                toast("Error occurred. Please check server logs", {
                    autoClose: 1500,
                    type: 'error',
                });
            }).finally(()=>{
                setIsSaveReportLoading(false);
                setOpenSaveConfirmationDialog(false);
                if(goBack && setCurrentView){
                    setCurrentView('search');
                }
            })
    }

    return (
        <Container maxWidth={false} sx={{ py: 3 }}>
            {/* Patient Info Header */}
            <RiskPatientInfoPanel
                assessmentData={assessmentData}
                handleDownload={handleDownload!}
                isView={isView}
                notifiedUsers={notifiedUsers}
                isLoading={isSaveReportLoading}
                setCurrentView={setCurrentView!}
                setOpenNotifyDialog={setOpenNotifyDialog}
                setOpenSaveConfirmationDialog={setOpenSaveConfirmationDialog}
                saveReports={saveReports}
                isPastSearch={isPastSearch}
            />

            {/* Reports to POM Support on this result (not part of the PDF render) */}
            {handleDownload && (!!sessionReferences.length || !!flags?.length) && (
                <Alert severity="info" icon={<SupportAgent />} sx={{ mb: 3 }}>
                    {sessionReferences.map((reference) => (
                        <div key={reference}>{`You reported this result to POM Support. Reference ${reference}.`}</div>
                    ))}
                    {flags?.filter((flag) => !sessionReferences.includes(flag.reference)).map((flag) => (
                        <div key={flag.id}>
                            {`${flag.reference} • ${flag.status === 'open' ? 'Reported to POM Support' : 'Resolved by POM Support'} by ${flag.createdBy ?? 'Removed user'}, ${dayjs(flag.createdAt).format('MMM D, YYYY h:mm A')}: ${flag.comment}`}
                        </div>
                    ))}
                </Alert>
            )}

            {/* 1. Key Risk Factors (why it was flagged) */}
            <RiskFactorsAccordionSection isDownloadView={!handleDownload} assessmentData={assessmentData} />

            {/* 2. Subject Sex Offender Match */}
            <SubjectOffenderMatch assessmentData={assessmentData}/>

            {/* 3. Sex Offenders at Location, other than the patient */}
            <OffendersAtLocation isDownloadView={!handleDownload} assessmentData={assessmentData}/>

            {/* 4. Criminal Records */}
            <PublicRecords handleDownload={!!handleDownload} assessmentData={assessmentData} showFilteredCharges={showFilteredCharges}/>

            {/* 5. Location & Area Safety */}
            <LocationAndArea assessmentData={assessmentData}/>

            {/* 6. Related News */}
            <NewsArticles handleDownload={!!handleDownload} assessmentData={assessmentData}/>

            {/* Report to POM Support: a rare action, so a quiet line after the
                report rather than a button beside Save/PDF/Notify. Not in the PDF. */}
            {handleDownload && canFlag && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3, px: 2, py: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <SupportAgent sx={{ color: 'text.secondary' }} />
                    <Typography variant="body1" color="text.secondary">
                        Something in this report looks wrong?
                    </Typography>
                    <Link component="button" type="button" variant="body1" fontWeight={600} underline="hover" onClick={() => setOpenFlagDialog(true)}>
                        Report it to POM Support
                    </Link>
                </Box>
            )}

            {/* Back Button */}
            {setCurrentView && (
                <Button
                    variant="outlined-alternate"
                    startIcon={<ArrowBack />}
                    onClick={() => setCurrentView('search')}
                    sx={{ mb: 3 }}
                >
                    Back to Search
                </Button>
            )}


            {/* Notify */}
            <NotifyTeamDialog
                open={openNotifyDialog}
                address={address.address}
                alreadyNotified={notifiedUsers}
                isLoading={isSaveReportLoading}
                onNotify={(users)=>{saveReports(users, true).catch((e) => console.error('[RiskAssessmentResult] notify save failed:', e))}}
                onClose={()=>{setOpenNotifyDialog(false)}}
            />

            <SaveReportConfirmDialog
                visible={openSaveConfirmationDialog}
                loading={isSaveReportLoading}
                title={"Do you want to save this Report?"}
                description={"This report has not been saved. Navigating away will discard it. Are you sure you want to leave?"}
                onConfirm={()=>{saveReports([],false,true)}}
                onCancel={()=>{setOpenSaveConfirmationDialog(false); setCurrentView!('search')}}
                onHide={()=>{setOpenSaveConfirmationDialog(false)}}
            />

            <FlagResultDialog
                open={openFlagDialog}
                target={flagTarget}
                onClose={() => setOpenFlagDialog(false)}
                onFlagged={(reference) => setSessionReferences((prev) => [...prev, reference])}
            />
        </Container>
    );
};